import React, { useEffect, useMemo, useState } from 'react';
import GenericModal from '../../../components/agv-generic-modal';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../../enums/modal-size.enum';
import LogPayloadDiff from './LogPayloadDiff';
import LogPayloadCodeEditor from './LogPayloadCodeEditor';
import { computeJsonDiff, parsePayload } from '../logs.utils';

const STEP = { EDIT: 'EDIT', REVIEW: 'REVIEW' };

/**
 * Correction du payload d'un envoi en erreur, en deux étapes : saisie puis relecture des différences.
 */
export default function LogPayloadEditorModal({
  isOpen,
  setIsOpen,
  log,
  onSave,
  isSaving,
}) {
  const [text, setText] = useState('');
  const [step, setStep] = useState(STEP.EDIT);
  const [retryAfterSave, setRetryAfterSave] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setText(JSON.stringify(log?.datas ?? {}, null, 2));
      setStep(STEP.EDIT);
      setRetryAfterSave(true);
    }
  }, [isOpen, log?.id]);

  const parsed = useMemo(() => parsePayload(text), [text]);
  const diff = useMemo(
    () => (parsed.value ? computeJsonDiff(log?.datas, parsed.value) : []),
    [parsed, log?.datas]
  );

  const save = async () => {
    const saved = await onSave(parsed.value, retryAfterSave);
    if (saved) {
      setIsOpen(false);
    }
  };

  const isEditStep = step === STEP.EDIT;

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={`Corriger les données — tournée ${log?.tour_key ?? ''}`}
      size={MODAL_SIZE_STYLE[MODAL_SIZE.XL]}
      secondaryActionText={isEditStep ? 'Annuler' : 'Retour à la saisie'}
      secondaryAction={() =>
        isEditStep ? setIsOpen(false) : setStep(STEP.EDIT)
      }
      mainActionText={
        isEditStep
          ? 'Vérifier les modifications'
          : retryAfterSave
            ? 'Enregistrer et relancer'
            : 'Enregistrer'
      }
      mainAction={isEditStep ? () => setStep(STEP.REVIEW) : save}
      mainActionDisabled={Boolean(parsed.error) || !diff.length}
      mainActionIsLoading={isSaving}
    >
      {isEditStep ? (
        <div className="tw-flex tw-flex-col tw-gap-2">
          <label
            htmlFor="tour-log-payload-editor"
            className="tw-text-sm tw-text-text-dark-medium-importance"
          >
            Données transmises à l’AS400 Cooperl, au format JSON. Corrigez les
            valeurs refusées (voir le message de l’AS400), puis vérifiez les
            modifications.
          </label>
          <LogPayloadCodeEditor
            id="tour-log-payload-editor"
            value={text}
            onChange={setText}
            invalid={Boolean(parsed.error)}
            describedBy="tour-log-payload-editor-status"
          />
          <p
            id="tour-log-payload-editor-status"
            className={`tw-m-0 tw-text-sm ${
              parsed.error
                ? 'tw-text-status-negative'
                : 'tw-text-text-dark-medium-importance'
            }`}
            role="status"
          >
            {parsed.error ||
              (diff.length
                ? `${diff.length} champ${diff.length > 1 ? 's' : ''} modifié${diff.length > 1 ? 's' : ''}.`
                : 'Aucune modification pour le moment.')}
          </p>
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <p className="tw-m-0 tw-text-sm">
            Vérifiez les modifications avant de les enregistrer. Elles seront
            tracées dans l’historique de l’envoi.
          </p>
          <LogPayloadDiff diff={diff} />
          <label className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-cursor-pointer">
            <input
              type="checkbox"
              checked={retryAfterSave}
              onChange={(event) => setRetryAfterSave(event.target.checked)}
              className="tw-mt-0.5"
            />
            <span>
              Relancer l’envoi après l’enregistrement
              <span className="tw-block tw-text-xs tw-text-text-dark-medium-importance">
                Les données corrigées seront transmises à l’AS400 Cooperl dans
                la minute, en un seul essai.
              </span>
            </span>
          </label>
        </div>
      )}
    </GenericModal>
  );
}
